import { useAuthStore } from '@/stores/authStore';

/**
 * Hook to check user permissions
 * 
 * @example
 * const { hasPermission, hasAnyPermission } = usePermissions();
 * 
 * // Check single permission
 * if (hasPermission('assets.delete')) { ... }
 * 
 * // Check if user has any of the permissions
 * if (hasAnyPermission('assets.create', 'assets.update')) { ... }
 * 
 * // Conditionally render based on permission
 * {hasPermission('users.create') && <CreateUserButton />}
 */
export function usePermissions() {
  const permissions = useAuthStore((state) => state.permissions);
  const user = useAuthStore((state) => state.user);
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const hasAnyPermission = useAuthStore((state) => state.hasAnyPermission);
  const hasAllPermissions = useAuthStore((state) => state.hasAllPermissions);

  return {
    permissions,
    role: user?.role,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    
    // Convenience checks for common permission patterns
    canView: (module: string) => hasPermission(`${module}.view`),
    canCreate: (module: string) => hasPermission(`${module}.create`),
    canUpdate: (module: string) => hasPermission(`${module}.update`),
    canDelete: (module: string) => hasPermission(`${module}.delete`),
  };
}

/**
 * All available permission codes
 * Use these constants to avoid typos
 */
export const PERMISSIONS = {
  // Assets
  ASSETS_VIEW: 'assets.view',
  ASSETS_CREATE: 'assets.create',
  ASSETS_UPDATE: 'assets.update',
  ASSETS_DELETE: 'assets.delete',
  ASSETS_TRANSFER: 'assets.transfer',
  ASSETS_REPAIR: 'assets.repair',
  ASSETS_DISPOSE: 'assets.dispose',
  
  // Users
  USERS_VIEW: 'users.view',
  USERS_CREATE: 'users.create',
  USERS_UPDATE: 'users.update',
  USERS_DELETE: 'users.delete',
  USERS_CHANGE_ROLE: 'users.change_role',
  USERS_RESET_PASSWORD: 'users.reset_password',
  
  // Employees
  EMPLOYEES_VIEW: 'employees.view',
  EMPLOYEES_CREATE: 'employees.create',
  EMPLOYEES_UPDATE: 'employees.update',
  EMPLOYEES_DELETE: 'employees.delete',
  
  // Tickets
  TICKETS_VIEW: 'tickets.view',
  TICKETS_CREATE: 'tickets.create',
  TICKETS_UPDATE: 'tickets.update',
  TICKETS_ASSIGN: 'tickets.assign',
  TICKETS_CLOSE: 'tickets.close',
  
  // Reports
  REPORTS_VIEW: 'reports.view',
  REPORTS_EXPORT: 'reports.export',
  
  // Settings
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_UPDATE: 'settings.update',
  
  // Audit
  AUDIT_VIEW: 'audit.view',
  
  // Organization
  ORGANIZATION_MANAGE: 'organization.manage',
} as const;

export type PermissionCode = typeof PERMISSIONS[keyof typeof PERMISSIONS];
